"use client";

import {
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";

import type { LazyTooltipImpl as LazyTooltipImplType } from "./tooltip-impl.client";
import { useRefocusOnSwap } from "./trigger-props";

type ImplProps = Parameters<typeof LazyTooltipImplType>[0];

/**
 * Floating-UI-free until hover/focus. Renders `renderTrigger` as a plain SSR'd
 * element; the real Base-UI Tooltip (and its Floating UI chunk) is `import()`ed
 * on the first hover/focus and swapped in only once loaded into state — the
 * trigger never blinks out. The impl opens on load ONLY if the pointer/focus is
 * still on the trigger (`activeRef`), so a hover that left during the async load
 * never flashes the tooltip open. Keeps Floating UI out of the eager bundle.
 */
export function LazyTooltip({
  renderTrigger,
  content,
  contentClassName,
}: {
  renderTrigger: (props: Record<string, unknown>) => ReactElement;
  content: ReactNode;
  contentClassName?: string;
}) {
  const [Impl, setImpl] = useState<ComponentType<ImplProps> | null>(null);
  // Pointer/focus currently on the trigger. Read when the chunk lands to decide
  // whether to open — `onPointerLeave`/`onBlur` clear it if the user left first.
  const activeRef = useRef(false);
  const armedRef = useRef(false);
  const refocus = useRefocusOnSwap();

  const arm = () => {
    activeRef.current = true;
    if (armedRef.current) return;
    armedRef.current = true;
    void import("./tooltip-impl.client").then((m) =>
      setImpl(() => m.LazyTooltipImpl as ComponentType<ImplProps>),
    );
  };

  const disarm = () => {
    activeRef.current = false;
  };

  if (!Impl) {
    return renderTrigger(
      refocus({
        onPointerEnter: arm,
        onFocus: arm,
        onPointerLeave: disarm,
        onBlur: disarm,
      }),
    );
  }

  return (
    <Impl
      defaultOpen={activeRef.current}
      renderTrigger={(props) => renderTrigger(refocus(props))}
      content={content}
      contentClassName={contentClassName}
    />
  );
}
